import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Mail, ArrowRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-zinc-900 text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block bg-white p-2 rounded">
              <img 
                src="/images/charles-print-logo.png" 
                alt="Charles Print Logo"
                className="h-12 w-auto"
                style={{ mixBlendMode: 'multiply' }}
              />
            </Link>
            <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
              Professional printing services in the heart of New York City. Quality you can trust, service you can rely on.
            </p>
            <div className="mt-6 flex items-center gap-2 text-[#FBBF24]">
              {[...Array(5)].map((_, i) => (
                <svg key={`footer-star-${i}`} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="ml-2 text-white text-sm font-medium">5-Star Reviews</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-sm font-bold tracking-widest uppercase mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Services', path: '/services' },
                { name: 'About Us', path: '/about' },
                { name: 'Reviews', path: '/reviews' },
                { name: 'Contact', path: '/contact' },
                { name: 'Get a Quote', path: '/quote' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-sm font-bold tracking-widest uppercase mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                'Business Cards',
                'Flyers & Brochures',
                'Posters & Banners',
                'Digital Printing',
                'Custom Print Jobs',
              ].map((service) => (
                <li key={service}>
                  <Link 
                    to="/services" 
                    className="text-zinc-400 hover:text-white transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-sm font-bold tracking-widest uppercase mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+12129711334" 
                  className="flex items-start gap-3 text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 mt-0.5 text-[#3EACDB]" />
                  <span>(212) 971-1334</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:Jobs@charlesprint.com" 
                  className="flex items-start gap-3 text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 mt-0.5 text-[#3EACDB]" />
                  <span>Jobs@charlesprint.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-zinc-400">
                <MapPin className="w-5 h-5 mt-0.5 text-[#3EACDB] flex-shrink-0" />
                <span>247 W 35th St, Suite #203<br />New York, NY 10001</span>
              </li>
              <li className="flex items-start gap-3 text-zinc-400">
                <Clock className="w-5 h-5 mt-0.5 text-[#3EACDB]" />
                <div>
                  <p>Mon - Fri: 9AM - 5:30PM</p>
                  <p>Sat - Sun: By Appointment</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            &copy; {currentYear} Charles Print. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <span>Professional Printing in NYC</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
